import { useReducer, useEffect } from 'react'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'
import Grid from '@mui/material/Unstable_Grid2'
import { backendPatchWithoutRefresh } from '../../../helpers/callBackend'
import { errorMessage } from '../../../helpers/errorMessageFunc'
import { useUcs } from '../../../helpers/contexts/UserCompanySettingsContext'

// Reducer to manage editing, error, and submitting states
const editLastFourReducer = (state, action) => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload }
    case 'SET_VALUE':
      return { ...state, value: action.payload }
    case 'SET_EDITING':
      return { ...state, isEditing: action.payload }
    case 'SET_NEW_VALUE':
      return { ...state, newVal: action.payload }
    case 'SET_ERROR':
      return { ...state, error: action.payload }
    case 'SET_SUBMITTING':
      return { ...state, submitting: action.payload }
    case 'RESET_FORM':
      return { ...state, isEditing: false, newVal: '', error: '', submitting: false }
    case 'SET_DASHBOARD_EDITOR':
      return { ...state, isDashboardEditor: action.payload }
    default:
      return state
  }
}

const EditLastFour = ({ bankAccountId, lastFour, accountNumber, allowEdit }) => {
  const initialState = {
    loading: true,
    value: lastFour,
    isEditing: false,
    newVal: '',
    error: '',
    submitting: false,
    isDashboardEditor: false,
  }
  const [state, dispatch] = useReducer(editLastFourReducer, initialState)

  const ucsContext = useUcs()

  useEffect(() => {
    if (ucsContext.loaded) {
      dispatch({ type: 'SET_DASHBOARD_EDITOR', payload: ucsContext.isDashboardEditor() })
    }
  }, [ucsContext])

  const validateAndSave = async () => {
    try {
      dispatch({ type: 'SET_SUBMITTING', payload: true })
      dispatch({ type: 'SET_ERROR', payload: '' })

      if (state.newVal.length < 4) {
        dispatch({ type: 'SET_ERROR', payload: 'Account last 4 must be at least 4 numbers long' })
        return
      }
      if (!/^\d+$/.test(state.newVal)) {
        dispatch({ type: 'SET_ERROR', payload: 'Account last 4 must be numeric' })
        return
      }
      if (accountNumber && /^\d+$/.test(state.newVal)) {
        if (!accountNumber.endsWith(state.newVal)) {
          dispatch({
            type: 'SET_ERROR',
            payload: 'New account number last 4 must match the end of the known account number',
          })
          return
        }
      }
      const data = {
        account_id: bankAccountId,
        action: 'update_last_four',
        last_four: state.newVal,
      }
      const lastFourResp = await backendPatchWithoutRefresh('/api/bank-account/', data)

      if (lastFourResp.status == 200) {
        dispatch({ type: 'SET_VALUE', payload: state.newVal })
        dispatch({ type: 'SET_EDITING', payload: false })
      }

      dispatch({ type: 'SET_SUBMITTING', payload: false })
    } catch (err) {
      dispatch({ type: 'SET_ERROR', payload: 'Save not successful. Try again.' })
      dispatch({ type: 'SET_SUBMITTING', payload: false })
    }
  }

  const handleSave = () => {
    validateAndSave(state.newVal)
  }

  const handleCancel = () => {
    dispatch({ type: 'RESET_FORM' })
  }

  const handleEdit = () => {
    dispatch({ type: 'SET_EDITING', payload: true })
    dispatch({ type: 'SET_ERROR', payload: '' })
  }

  const handleInputChange = (e) => {
    dispatch({ type: 'SET_NEW_VALUE', payload: e.target.value })
  }

  useEffect(() => {
    if (state.error.length > 0) {
      errorMessage(state.error)
      dispatch({ type: 'SET_ERROR', payload: '' })
      dispatch({ type: 'SET_SUBMITTING', payload: false })
    }
  }, [state.error])

  const textFieldStyle = {
    fullWidth: true,
    size: 'small',
    InputProps: {
      style: {
        borderRadius: 0,
        maxHeight: '32px',
        maxWidth: '124px',
      },
    },
  }

  const renderField = () => {
    if (!allowEdit || !state.isDashboardEditor) {
      return (
        <Grid container direction='row' spacing={1} alignItems='center'>
          <Typography>{state.value}</Typography>
        </Grid>
      )
    }
    if (state.isEditing) {
      return (
        <Grid container direction='row' spacing={1} alignItems='center'>
          <Grid item>
            <TextField defaultValue={state.value} onChange={handleInputChange} {...textFieldStyle} />
          </Grid>
          <Grid item>
            <Button
              sx={{ fontSize: 12, textTransform: 'none' }}
              disableElevation
              fullWidth
              size='small'
              type='submit'
              variant='contained'
              color='primary'
              onClick={handleSave}
              disabled={!state.newVal || state.submitting}
            >
              {state.submitting ? 'Saving...' : 'Save'}
            </Button>
          </Grid>
          <Grid item>
            <Button
              sx={{ fontSize: 12, textTransform: 'none' }}
              disableElevation
              fullWidth
              size='small'
              type='submit'
              variant='outlined'
              color='secondary'
              onClick={handleCancel}
              disabled={state.submitting}
            >
              Cancel
            </Button>
          </Grid>
        </Grid>
      )
    } else {
      return (
        <Grid container direction='row' spacing={1} alignItems='center'>
          <Grid item>
            <Typography>{state.value}</Typography>
          </Grid>
          <Grid item>
            <Button
              sx={{ fontSize: 12, textTransform: 'none' }}
              disableElevation
              fullWidth
              size='small'
              type='submit'
              variant='outlined'
              color='secondary'
              onClick={handleEdit}
            >
              Edit
            </Button>
          </Grid>
        </Grid>
      )
    }
  }

  return (
    <Grid container direction='row' sx={{ marginBottom: '1em' }} justifyContent='space-between' alignItems='center'>
      <Typography>Account Number Last 4</Typography>
      <>{renderField()}</>
    </Grid>
  )
}

export default EditLastFour
